import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	ExcelExport,
	ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { useTranslation } from "react-i18next";

import {
	getPackageLinesApi,
	getPackagesNotReleasedApi,
	PackagesNotReleasedFilter,
} from "api/package";
import { PackagesNotReleased } from "interface/package";
import styles from "styles.module.css";
import CustomDetailedPage from "ui/grid/DetailPage";
import GridPageNoPaging from "ui/grid/GridPageNoPaging";

import Filter from "./Filter";
import {
	packageLineDetailGridItems,
	packagesNotReleasedGridColumnItem,
} from "./GridColumns";
import UnReleasedPackagesMobileList from "./UnReleasedPackagesMobileList";

const pageSize = 60;

type PackagesNotReleasedGridItem = PackagesNotReleased & {
	packageKey: string;
};

const UnReleasedPackagePage = () => {
	const { t } = useTranslation();
	const [filterOptions, setFilterOptions] = useState<PackagesNotReleasedFilter>(
		{},
	);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<PackagesNotReleased[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const [loadedGridData, setLoadedGridData] = useState<PackagesNotReleased[]>(
		[],
	);
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const mobileExcelRef = useRef<ExcelExport>(null);

	const getPackages = useCallback(async (filter: PackagesNotReleasedFilter) => {
		const response = await getPackagesNotReleasedApi(filter);
		response.data = (response.data || []).map(
			(item): PackagesNotReleasedGridItem => ({
				...item,
				packageKey: `${item.registrationId}-${item.packageId}`,
			}),
		);
		return response;
	}, []);

	const handleLoadedGridData = useCallback((data: PackagesNotReleased[]) => {
		setLoadedGridData(data);
	}, []);

	const gridColumns = useMemo(
		() => packagesNotReleasedGridColumnItem(loadedGridData),
		[loadedGridData],
	);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);
		return () => window.removeEventListener("resize", updateMobileView);
	}, []);

	useEffect(() => {
		if (!isMobileView) return;

		let active = true;
		setMobileLoading(true);
		getPackagesNotReleasedApi(filterOptions)
			.then((response) => {
				if (active) setMobileData(response?.data || []);
			})
			.finally(() => {
				if (active) setMobileLoading(false);
			});

		return () => {
			active = false;
		};
	}, [filterOptions, isMobileView]);

	const exportAsExcel = () => {
		if (isMobileView) {
			mobileExcelRef.current?.save();
			return;
		}
		excelRef.current?.save();
	};

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("PackagesNotReleased")}
				</legend>
			</div>

			<ExcelExport
				ref={mobileExcelRef}
				data={mobileData}
				fileName="packages_not_released.xlsx"
			>
				<ExcelExportColumn
					field="registrationId"
					title={t("RegistrationIdShort")}
				/>
				<ExcelExportColumn field="packageId" title={t("Package")} />
				<ExcelExportColumn
					field="salesDate"
					title={t("SalesDate")}
					cellOptions={{ format: "dd.mm.yyyy" }}
				/>
				<ExcelExportColumn
					field="vesselRegistrationId"
					title={t("VesselRegistrationId")}
				/>
				<ExcelExportColumn field="vesselName" title={t("VesselName")} />
				<ExcelExportColumn field="buyerName" title={t("BuyerName")} />
				<ExcelExportColumn
					field="registrationDate"
					title={t("RegistrationDateShort")}
					cellOptions={{ format: "dd.mm.yyyy" }}
				/>
				<ExcelExportColumn field="salesType" title={t("Type")} />
				<ExcelExportColumn field="liveWeightKg" title={t("Weight")} />
				<ExcelExportColumn field="grossAmount" title={t("BuyerGrossAmount")} />
				<ExcelExportColumn field="vatAmount" title={t("Vat")} />
				<ExcelExportColumn field="netAmount" title={t("BuyerNetAmount")} />
			</ExcelExport>

			<Filter
				setFilterOptions={setFilterOptions}
				filterOptions={filterOptions}
				exportAsPDF={() => pdfRef.current?.save()}
				exportAsExcel={exportAsExcel}
				pageSize={pageSize}
			/>

			<div style={{ marginTop: 30 }}>
				{isMobileView ? (
					<div
						style={{
							height: "calc(100vh - 450px)", // Reduced from 150px to fit screen better
							overflow: "auto",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<UnReleasedPackagesMobileList
							items={mobileData}
							loading={mobileLoading}
						/>
					</div>
				) : (
					<GridPageNoPaging
						dataKey="packageKey"
						gridColumns={gridColumns}
						detailPage={(props) => (
							<CustomDetailedPage
								grids={packageLineDetailGridItems}
								dataApi={() =>
									getPackageLinesApi({
										registrationId: props.dataItem.registrationId,
										packageId: props.dataItem.packageId,
									})
								}
							/>
						)}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={getPackages}
						getloadedData={handleLoadedGridData}
						lastColWidth="60px"
						itemAboveHeight={60}
						sortable={{ allowUnsort: true, mode: "single" }}
						excelProps={{ fileName: "packages_not_released.xlsx" }}
					/>
				)}
			</div>
		</div>
	);
};

export default UnReleasedPackagePage;
